import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  DollarOutlined,
  UserOutlined,
  ShopOutlined,
  CompassOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  MailOutlined,
  BarChartOutlined,
  ContactsOutlined,
  WarningOutlined,
  CrownOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { Drawer, Alert } from 'antd';

import Avatar from 'components/Avatar';
import { OrangeButton } from 'components/Styled/v2/OrangeButton';
import {
  ContentaLogoV2,
  LogoutIcon,
  TravelAgencyIcon,
  TravelerIcon,
  TravelExpertIcon,
} from '../NavbarV2/icons';

import { useUser } from 'contexts/userContext';
import useMissingSteps from 'hooks/useMissingSteps';
import usePendingActions from 'hooks/usePendingActions';
import useUnreadChats from 'hooks/useUnreadChats';
import useIsB2BLandingPage from 'hooks/useIsB2BLandingPage';

import {
  isUserAdmin,
  isUserGuide,
  isUserTraveller,
  canViewMaster,
  canViewBookingAgent,
  isB2BUser,
} from 'utils/isRole';
import { FEATURES } from 'constants/feature-flags';
import { toggleEventManager } from 'utils/toggleDrawer';
import { truncateString } from 'utils/truncateString';
import { scrollToId } from 'utils/scrollToId';
import { trackB2BEvent } from 'utils/b2bAnalytics';

import {
  ADMIN_ROUTES,
  B2B_ROUTES,
  MENTOR_ROUTES,
  PUBLIC_ROUTES,
  TRAVELLER_ROUTES,
} from 'constants/routes';

import './styles.scss';

const icons = {
  aboutUs: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 21.25C17.1086 21.25 21.25 17.1086 21.25 12C21.25 6.89137 17.1086 2.75 12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 17.1086 6.89137 21.25 12 21.25Z"
        stroke="#001F3E"
        strokeWidth="1.5"
      />
      <path
        d="M12 11.813V16.813"
        stroke="#001F3E"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M12 9.68799C12.6904 9.68799 13.25 9.12834 13.25 8.43799C13.25 7.74763 12.6904 7.18799 12 7.18799C11.3096 7.18799 10.75 7.74763 10.75 8.43799C10.75 9.12834 11.3096 9.68799 12 9.68799Z"
        fill="#001F3E"
      />
    </svg>
  ),

  home: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.21429 19.765H9.69743V13.4951C9.69743 13.2127 9.79174 12.976 9.98036 12.785C10.1682 12.5932 10.4009 12.4972 10.6786 12.4972H14.3214C14.5991 12.4972 14.8322 12.5932 15.0209 12.785C15.2087 12.976 15.3026 13.2127 15.3026 13.4951V19.765H19.7857V9.03068C19.7857 8.90389 19.7586 8.78863 19.7044 8.68489C19.6501 8.58115 19.576 8.49059 19.4821 8.41319L12.9444 3.40169C12.8198 3.29137 12.6716 3.2362 12.5 3.2362C12.3284 3.2362 12.1806 3.29137 12.0568 3.40169L5.51786 8.41319C5.42476 8.49223 5.35069 8.5828 5.29564 8.68489C5.2406 8.78698 5.21348 8.90224 5.21429 9.03068V19.765ZM4 19.765V9.03068C4 8.71453 4.06962 8.41525 4.20886 8.13286C4.3481 7.85046 4.53995 7.61787 4.78443 7.4351L11.3234 2.3989C11.6658 2.13297 12.0568 2 12.4964 2C12.9359 2 13.3294 2.13297 13.6766 2.3989L20.2156 7.43387C20.4609 7.61664 20.6527 7.84964 20.7911 8.13286C20.9304 8.41525 21 8.71453 21 9.03068V19.765C21 20.096 20.879 20.3846 20.6369 20.6307C20.3949 20.8769 20.1111 21 19.7857 21H15.0694C14.791 21 14.5578 20.9045 14.37 20.7135C14.1822 20.5217 14.0883 20.2845 14.0883 20.0021V13.7334H10.9117V20.0021C10.9117 20.2854 10.8178 20.5225 10.63 20.7135C10.4422 20.9045 10.2095 21 9.93179 21H5.21429C4.88886 21 4.60512 20.8769 4.36307 20.6307C4.12102 20.3846 4 20.096 4 19.765Z"
        fill="#001F3E"
      />
    </svg>
  ),

  history: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.07934 5.06902C8.87434 1.27902 15.0443 1.31902 18.8623 5.13802C22.6823 8.95802 22.7223 15.131 18.9263 18.926C15.1303 22.721 8.95834 22.682 5.13834 18.862C4.06371 17.792 3.25177 16.4875 2.76629 15.0508C2.28081 13.6142 2.13501 12.0845 2.34034 10.582C2.36726 10.3848 2.47141 10.2064 2.62988 10.086C2.78835 9.96562 2.98815 9.9131 3.18534 9.94002C3.38253 9.96694 3.56095 10.0711 3.68134 10.2296C3.80174 10.388 3.85426 10.5878 3.82734 10.785C3.65302 12.0583 3.77642 13.3547 4.18782 14.5723C4.59923 15.7899 5.28744 16.8954 6.19834 17.802C9.44334 21.046 14.6663 21.065 17.8663 17.866C21.0653 14.666 21.0463 9.44302 17.8023 6.19802C14.5593 2.95602 9.33934 2.93502 6.13934 6.13002L6.88734 6.13302C6.98583 6.13348 7.08327 6.15334 7.17409 6.19145C7.2649 6.22957 7.34733 6.2852 7.41664 6.35517C7.48596 6.42514 7.54082 6.50807 7.57809 6.59924C7.61536 6.69041 7.6343 6.78803 7.63384 6.88652C7.63338 6.98501 7.61353 7.08245 7.57541 7.17327C7.53729 7.26409 7.48166 7.34651 7.41169 7.41583C7.34173 7.48515 7.25879 7.54 7.16762 7.57727C7.07645 7.61454 6.97883 7.63348 6.88034 7.63302L4.33434 7.62102C4.13664 7.61997 3.94735 7.5409 3.80765 7.40101C3.66795 7.26112 3.58913 7.07172 3.58834 6.87402L3.57534 4.33002C3.57481 4.23153 3.59369 4.1339 3.6309 4.0427C3.6681 3.95151 3.72291 3.86854 3.79218 3.79852C3.86145 3.7285 3.94384 3.67282 4.03463 3.63464C4.12542 3.59647 4.22285 3.57655 4.32134 3.57602C4.41983 3.5755 4.51746 3.59438 4.60866 3.63158C4.69985 3.66879 4.78283 3.72359 4.85284 3.79286C4.92286 3.86214 4.97854 3.94452 5.01672 4.03531C5.05489 4.12611 5.07481 4.22353 5.07534 4.32202L5.07934 5.06902ZM11.9993 7.24902C12.1983 7.24902 12.389 7.32804 12.5297 7.46869C12.6703 7.60934 12.7493 7.80011 12.7493 7.99902V11.689L15.0303 13.969C15.102 14.0382 15.1592 14.1209 15.1985 14.2124C15.2379 14.3039 15.2586 14.4023 15.2595 14.5019C15.2604 14.6014 15.2415 14.7002 15.2038 14.7924C15.1662 14.8846 15.1105 14.9684 15.0401 15.0388C14.9698 15.1093 14.886 15.165 14.7939 15.2027C14.7017 15.2405 14.603 15.2595 14.5034 15.2587C14.4038 15.2579 14.3054 15.2372 14.2139 15.198C14.1223 15.1587 14.0396 15.1016 13.9703 15.03L11.2503 12.31V8.00002C11.2503 7.80111 11.3294 7.61034 11.47 7.46969C11.6107 7.32904 11.8014 7.25002 12.0003 7.25002"
        fill="#001F3E"
      />
    </svg>
  ),
  chat: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.97005 9.88601H16.03M7.97005 14.114H13.718M12 21.25C14.0094 21.2496 15.964 20.5948 17.5682 19.3848C19.1725 18.1748 20.339 16.4753 20.8915 14.5434C21.444 12.6115 21.3524 10.5521 20.6305 8.6769C19.9086 6.80167 18.5958 5.21249 16.8904 4.14971C15.1851 3.08694 13.1801 2.60837 11.1786 2.78638C9.17712 2.96439 7.28803 3.7893 5.79703 5.13636C4.30603 6.48341 3.29422 8.27934 2.91462 10.2525C2.53501 12.2258 2.80827 14.2689 3.69305 16.073C3.80105 16.293 3.83705 16.541 3.78205 16.779L2.96605 20.315C2.94304 20.4142 2.94568 20.5177 2.97372 20.6156C3.00175 20.7136 3.05425 20.8027 3.12628 20.8748C3.19831 20.9468 3.28749 20.9993 3.38543 21.0273C3.48336 21.0554 3.58681 21.058 3.68605 21.035L7.22105 20.218C7.45972 20.1657 7.70912 20.1975 7.92705 20.308C9.19444 20.9304 10.5881 21.2527 12 21.25Z"
        stroke="#001F3E"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  faq: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.5005 7.33333C10.9435 7.33333 10.4093 7.54405 10.0154 7.91912C9.62149 8.29419 9.4002 8.8029 9.4002 9.33333V9.476C9.4002 9.65281 9.32644 9.82238 9.19515 9.9474C9.06386 10.0724 8.88578 10.1427 8.7001 10.1427C8.51442 10.1427 8.33635 10.0724 8.20505 9.9474C8.07376 9.82238 8 9.65281 8 9.476V9.33333C8 8.44928 8.3688 7.60143 9.02528 6.97631C9.68175 6.35119 10.5721 6 11.5005 6H11.6629C12.3447 6.0002 13.0101 6.19918 13.5695 6.57019C14.129 6.94119 14.5558 7.46643 14.7924 8.07527C15.0291 8.68411 15.0643 9.34736 14.8933 9.97582C14.7223 10.6043 14.3534 11.1678 13.8361 11.5907L12.7565 12.472C12.5824 12.6145 12.4426 12.791 12.3467 12.9896C12.2508 13.1881 12.2009 13.4041 12.2006 13.6227V14.3333C12.2006 14.5101 12.1269 14.6797 11.9956 14.8047C11.8643 14.9298 11.6862 15 11.5005 15C11.3148 15 11.1368 14.9298 11.0055 14.8047C10.8742 14.6797 10.8004 14.5101 10.8004 14.3333V13.6227C10.8004 12.7907 11.1827 12.0013 11.845 11.46L12.9231 10.58C13.2238 10.3347 13.4383 10.0076 13.5379 9.64266C13.6374 9.27775 13.6172 8.89256 13.48 8.53891C13.3427 8.18527 13.095 7.88015 12.7701 7.66461C12.4453 7.44908 12.0589 7.33346 11.6629 7.33333H11.5005ZM11.5005 18C11.779 18 12.0461 17.8946 12.2431 17.7071C12.44 17.5196 12.5507 17.2652 12.5507 17C12.5507 16.7348 12.44 16.4804 12.2431 16.2929C12.0461 16.1054 11.779 16 11.5005 16C11.222 16 10.9549 16.1054 10.7579 16.2929C10.561 16.4804 10.4504 16.7348 10.4504 17C10.4504 17.2652 10.561 17.5196 10.7579 17.7071C10.9549 17.8946 11.222 18 11.5005 18Z"
        fill="#001F3E"
      />
      <path
        d="M2 12C2 9.34784 3.05357 6.8043 4.92893 4.92893C6.8043 3.05357 9.34784 2 12 2C14.6522 2 17.1957 3.05357 19.0711 4.92893C20.9464 6.8043 22 9.34784 22 12C22 14.6522 20.9464 17.1957 19.0711 19.0711C17.1957 20.9464 14.6522 22 12 22C9.34784 22 6.8043 20.9464 4.92893 19.0711C3.05357 17.1957 2 14.6522 2 12ZM12 3.17647C9.65985 3.17647 7.41556 4.10609 5.76082 5.76082C4.10609 7.41556 3.17647 9.65985 3.17647 12C3.17647 14.3401 4.10609 16.5844 5.76082 18.2392C7.41556 19.8939 9.65985 20.8235 12 20.8235C14.3401 20.8235 16.5844 19.8939 18.2392 18.2392C19.8939 16.5844 20.8235 14.3401 20.8235 12C20.8235 9.65985 19.8939 7.41556 18.2392 5.76082C16.5844 4.10609 14.3401 3.17647 12 3.17647Z"
        fill="#001F3E"
      />
    </svg>
  ),
  logout: <LogoutIcon />,
  alert: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_450_20022)">
        <path
          d="M11.9999 1.66663C6.11992 1.66663 1.33325 5.85329 1.33325 11C1.33325 16.1466 6.11992 20.3333 11.9999 20.3333C13.1 20.332 14.1947 20.1795 15.2533 19.88L18.9399 22.2266C19.0406 22.2912 19.1567 22.3275 19.2762 22.3318C19.3957 22.336 19.5142 22.308 19.6192 22.2508C19.7241 22.1935 19.8118 22.1091 19.8729 22.0063C19.934 21.9035 19.9664 21.7862 19.9666 21.6666V17.18C20.8007 16.3797 21.4664 15.4207 21.9244 14.3594C22.3823 13.2981 22.6234 12.1558 22.6333 11C22.6666 5.85329 17.8799 1.66663 11.9999 1.66663ZM11.2866 6.08663C11.2756 5.95698 11.2917 5.82647 11.3338 5.70336C11.3759 5.58025 11.4431 5.46722 11.5312 5.37143C11.6192 5.27564 11.7262 5.19917 11.8453 5.14685C11.9645 5.09454 12.0931 5.06753 12.2233 5.06753C12.3534 5.06753 12.4821 5.09454 12.6012 5.14685C12.7203 5.19917 12.8273 5.27564 12.9153 5.37143C13.0034 5.46722 13.0706 5.58025 13.1127 5.70336C13.1548 5.82647 13.1709 5.95698 13.1599 6.08663V12.6C13.1709 12.7296 13.1548 12.8601 13.1127 12.9832C13.0706 13.1063 13.0034 13.2194 12.9153 13.3152C12.8273 13.4109 12.7203 13.4874 12.6012 13.5397C12.4821 13.592 12.3534 13.6191 12.2233 13.6191C12.0931 13.6191 11.9645 13.592 11.8453 13.5397C11.7262 13.4874 11.6192 13.4109 11.5312 13.3152C11.4431 13.2194 11.3759 13.1063 11.3338 12.9832C11.2917 12.8601 11.2756 12.7296 11.2866 12.6V6.08663ZM12.2266 17.6533C11.98 17.6533 11.739 17.5802 11.534 17.4432C11.329 17.3062 11.1692 17.1115 11.0748 16.8837C10.9805 16.6559 10.9558 16.4052 11.0039 16.1634C11.052 15.9216 11.1707 15.6994 11.3451 15.5251C11.5194 15.3507 11.7415 15.232 11.9834 15.1839C12.2252 15.1358 12.4759 15.1605 12.7037 15.2549C12.9315 15.3492 13.1262 15.509 13.2632 15.714C13.4001 15.919 13.4733 16.1601 13.4733 16.4066C13.4715 16.7361 13.3394 17.0515 13.1058 17.2838C12.8722 17.5162 12.5561 17.6466 12.2266 17.6466V17.6533Z"
          fill="#FFCD1C"
        />
      </g>
      <defs>
        <clipPath id="clip0_450_20022">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),
  search: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.8004 9.8C13.6782 9.8 15.2004 8.27777 15.2004 6.4C15.2004 4.52223 13.6782 3 11.8004 3C9.92262 3 8.40039 4.52223 8.40039 6.4C8.40039 8.27777 9.92262 9.8 11.8004 9.8Z"
        fill="white"
      />
      <path
        d="M18.6 16.1751C18.6 18.2873 18.6 20.0001 11.8 20.0001C5 20.0001 5 18.2873 5 16.1751C5 14.0628 8.0447 12.3501 11.8 12.3501C15.5553 12.3501 18.6 14.0628 18.6 16.1751Z"
        fill="white"
      />
    </svg>
  ),
};

const MenuFooter = () => {
  const { user, whoAmI, userRole, logout } = useUser();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isB2BLandingPage = useIsB2BLandingPage();

  if (!userRole) {
    if (pathname.startsWith('/b2b/')) return null;

    return (
      <footer className="menu-footer menu-footer__unlogged">
        <span className="menu-footer__label">Login</span>
        <Link className="d-flex-row gap-8 align-center" to="/login">
          <TravelerIcon />
          {t('buttons.loginTraveler')}
        </Link>
        <Link
          className="d-flex-row gap-8 align-center"
          to="/login/travel-expert"
        >
          <TravelExpertIcon />
          {t('buttons.loginAdvisor')}
        </Link>
        <Link
          className="d-flex-row gap-8 align-center"
          to="/login/travel-expert"
        >
          <TravelerIcon />
          {t('buttons.signUpAsTravelExpert')}
        </Link>
        {isB2BLandingPage && (
          <Link
            className="d-flex-row gap-8 align-center"
            to={B2B_ROUTES.LOGIN}
            onClick={() =>
              trackB2BEvent('b2b_login_click', { section: 'mobile-menu' })
            }
          >
            <TravelAgencyIcon />
            {t('b2b.landing.header.loginAsTravelAgency')}
          </Link>
        )}
        <div className="app-version">
          <span>v{__APP_VERSION__}</span>
        </div>
      </footer>
    );
  }

  return (
    <div className="menu-footer__content">
      <AlertMenu />
      <footer className="menu-footer menu-footer__logged">
        <div className="menu-footer__account">
          <Avatar
            src={whoAmI?.imageURL || whoAmI?.picture}
            className="menu-footer__avatar"
          />
          <div className="menu-footer__container">
            <span className="menu-footer__username">
              {truncateString(user?.name)}
            </span>
            <span className="menu-footer__email">
              {truncateString(user?.email)}
            </span>
            {/* ponytail: admin nao tem perfil publico para editar */}
            {!isUserAdmin(userRole) && (
              <Link
                className="menu-footer__profile"
                to={isUserGuide(userRole) ? MENTOR_ROUTES.PROFILE : 'profile'}
              >
                {t('sidebar.profile')}
              </Link>
            )}
          </div>
        </div>
        <button
          type="button"
          className="menu-footer__logout"
          onClick={logout}
          aria-label={t('common.logout')}
        >
          <LogoutIcon />
        </button>
      </footer>
      <div className="app-version">
        <span>v{__APP_VERSION__}</span>
      </div>
    </div>
  );
};

const AlertMenu = () => {
  const { userRole } = useUser();
  const hasMissingSteps = useMissingSteps();
  const { t } = useTranslation();

  const canShowAlert = isUserGuide(userRole) && hasMissingSteps;

  if (!canShowAlert) return null;

  return (
    <Alert
      className="alert-menu"
      icon={icons.alert}
      message={
        <span className="alert-menu__message">
          {t('mentorHome.alert.completeProfileTitle')}
        </span>
      }
      description={
        <>
          <span className="alert-menu__description">
            {t('mentorHome.alert.completeProfileDescription')}
          </span>
          <Link
            to={MENTOR_ROUTES.COMPLETE_PROFILE}
            className="alert-menu__link"
          >
            {t('mentorHome.alert.completeProfileLink')}
          </Link>
        </>
      }
      type="warning"
      showIcon
      closable={false}
    />
  );
};

export function SiderApp() {
  const { userRole, whoAmI } = useUser();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);

  // Admins have no chat entry at all, so they should not poll for one either.
  const { unreadCount } = useUnreadChats(!!userRole && !isUserAdmin(userRole));
  // Only the traveller has a page to land on — /pending-briefings lives behind
  // the traveller guard, and the advisor fills briefings from their own agenda.
  const { pendingBriefings } = usePendingActions(
    isUserTraveller(userRole) ? userRole : null
  );

  function redirectUrl(url) {
    if (url.startsWith('#')) {
      setOpen(false);
      // Let the drawer close before scrolling, or the close animation fights
      // scrollIntoView for the final resting position.
      setTimeout(() => scrollToId(url.slice(1)), 0);
      return;
    }

    navigate(url);
    setOpen(false);
  }

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const adminMenuList = [
    {
      key: 'admin-registrations',
      icon: <UserOutlined />,
      title: t('sidebar.admin.home'),
      url: '/admin',
    },
    {
      key: 'admin-prices',
      icon: <DollarOutlined />,
      title: t('sidebar.admin.prices'),
      url: '/admin/prices',
    },
    {
      key: 'admin-faq',
      icon: <QuestionCircleOutlined />,
      title: t('sidebar.admin.faq'),
      url: '/admin/faq',
    },
    {
      key: 'admin-newsletter',
      icon: <MailOutlined />,
      title: t('sidebar.admin.newsletter'),
      url: '/admin/newsletter',
    },
    {
      key: 'admin-organizations',
      icon: <ShopOutlined />,
      title: t('sidebar.admin.organizations'),
      url: ADMIN_ROUTES.ORGANIZATIONS,
    },
    {
      key: 'admin-partner-status',
      icon: <CrownOutlined />,
      title: t('sidebar.admin.partnerStatus'),
      url: ADMIN_ROUTES.PARTNER_STATUS,
    },
  ];

  const adminReportsMenuList = [
    {
      key: 'admin-reports',
      icon: <BarChartOutlined />,
      title: t('sidebar.admin.reports'),
      url: ADMIN_ROUTES.REPORTS,
    },
    {
      key: 'admin-reports-leads',
      icon: <ContactsOutlined />,
      title: t('sidebar.admin.reportsLeads'),
      url: ADMIN_ROUTES.REPORTS_LEADS,
    },
    {
      key: 'admin-reports-advisors',
      icon: <CompassOutlined />,
      title: t('sidebar.admin.reportsAdvisors'),
      url: ADMIN_ROUTES.REPORTS_ADVISORS,
    },
    {
      key: 'admin-reports-travelers',
      icon: <TeamOutlined />,
      title: t('sidebar.admin.reportsTravelers'),
      url: ADMIN_ROUTES.REPORTS_TRAVELERS,
    },
    {
      key: 'admin-reports-complaints',
      icon: <WarningOutlined />,
      title: t('sidebar.admin.reportsComplaints'),
      url: ADMIN_ROUTES.REPORTS_COMPLAINTS,
    },
  ];

  const loggedList = [
    {
      key: 'historico',
      icon: icons.history,
      title: t('sidebar.history'),
      url: '/history-calls',
    },
    // The briefing is what makes the call worth paying for, so the count sits
    // in the menu rather than waiting to be discovered on the page.
    ...(isUserTraveller(userRole) && pendingBriefings.length > 0
      ? [
          {
            key: 'pending-briefings',
            icon: <FormOutlined />,
            title: t('sidebar.pendingBriefings'),
            url: TRAVELLER_ROUTES.PENDING_BRIEFINGS,
            badge: pendingBriefings.length,
          },
        ]
      : []),
    ...(isUserAdmin(userRole)
      ? []
      : [
          {
            key: 'chats',
            icon: icons.chat,
            title: 'Chats',
            url: '/chat',
            badge: unreadCount,
          },
        ]),
    ...(isUserGuide(userRole)
      ? [
          {
            key: 'advisor-performance',
            icon: <BarChartOutlined />,
            title: t('performance.title'),
            url: MENTOR_ROUTES.PERFORMANCE,
          },
        ]
      : []),
    ...(canViewMaster(whoAmI)
      ? [
          {
            key: 'b2b-admin',
            icon: <TeamOutlined />,
            title: 'Painel B2B',
            url: B2B_ROUTES.ADMIN,
          },
        ]
      : []),
  ];

  const isB2BLandingPage = useIsB2BLandingPage();

  // On the B2B landing page itself, "How It Works" / "For Travel Agencies"
  // pointing back at this same page is dead weight — swap in the page's own
  // anchors (Agency Pricing instead of a self-link) per the b2b.landing spec.
  const unloggedMenuList = [
    {
      key: 'home',
      icon: icons.home,
      title: t('sidebar.home'),
      url: userRole
        ? isUserTraveller(userRole)
          ? TRAVELLER_ROUTES.HOME
          : MENTOR_ROUTES.HOME
        : '/',
    },
    {
      key: 'about-us',
      icon: icons.aboutUs,
      title: isB2BLandingPage
        ? t('b2b.landing.header.howItWorks')
        : t('common.nav.howItWorks'),
      url: isB2BLandingPage ? '#how-it-works' : '/#how-it-works',
    },
    ...(isB2BLandingPage
      ? [
          {
            key: 'agency-pricing',
            icon: <ShopOutlined />,
            title: t('b2b.landing.header.agencyPricing'),
            url: '#partnership',
          },
        ]
      : FEATURES.b2b
        ? [
            {
              key: 'travel-agencies',
              icon: <ShopOutlined />,
              title: t('common.nav.travelAgencies'),
              url: '/travelpartners',
            },
          ]
        : []),
    {
      key: 'tour-guides',
      icon: <CompassOutlined />,
      title: isB2BLandingPage
        ? t('b2b.landing.header.forLocalGuides')
        : t('common.nav.tourGuides'),
      url: '/travel-expert',
    },
    {
      key: 'faq',
      icon: icons.faq,
      title: t('sidebar.faq'),
      url: '/faq',
    },
  ];

  const isB2B = isB2BUser(whoAmI);
  const isMaster = canViewMaster(whoAmI);
  const isAgent = canViewBookingAgent(whoAmI);

  const b2bMenuList = [
    ...(isMaster
      ? [
          {
            key: 'b2b-team',
            icon: <TeamOutlined />,
            title: t('b2b.admin.title', { defaultValue: 'Team Management' }),
            url: B2B_ROUTES.ADMIN,
          },
          {
            key: 'b2b-agency-bookings',
            icon: icons.history,
            title: t('b2b.admin.bookings'),
            url: `${B2B_ROUTES.ADMIN}?tab=bookings`,
          },
        ]
      : []),
    ...(isAgent && !isMaster
      ? [
          {
            key: 'b2b-bookings',
            icon: icons.history,
            title: t('b2b.dashboard.myBookings'),
            url: B2B_ROUTES.AGENT_BOOKINGS,
          },
        ]
      : []),
    ...(isAgent
      ? [
          {
            key: 'b2b-new-reservation',
            icon: icons.search,
            title: t('b2b.dashboard.newReservation'),
            url: B2B_ROUTES.AGENT_NEW_RESERVATION,
          },
        ]
      : []),
  ];

  const [home, ...restUnlogged] = unloggedMenuList;

  // Os CRUDs de admin e as páginas públicas viram grupos rotulados: sem isso
  // "FAQ" do painel e "FAQ" do site ficam lado a lado, iguais e indistinguíveis.
  const menuList = isB2B
    ? b2bMenuList
    : userRole
      ? [
          home,
          ...loggedList,
          ...(isUserAdmin(userRole)
            ? [
                { key: 'section-admin', section: t('sidebar.section.admin') },
                ...adminMenuList,
                {
                  key: 'section-admin-reports',
                  section: t('sidebar.section.reports'),
                },
                ...adminReportsMenuList,
              ]
            : []),
          { key: 'section-site', section: t('sidebar.section.site') },
          ...restUnlogged,
        ]
      : unloggedMenuList;

  useEffect(() => {
    toggleEventManager.on('toggledrawer', toggleDrawer);

    return () => {
      toggleEventManager.removeListener('toggledrawer', toggleDrawer);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <Drawer
      className="lateral-menu"
      placement="left"
      onClose={() => setOpen(false)}
      open={open}
      closable={false}
      width={410}
    >
      <div className="lateral-menu__logo">
        <ContentaLogoV2 />
      </div>

      <div className="menu-body">
        {menuList.map((item) =>
          item.section ? (
            <p className="lateral-menu__section" key={item.key}>
              {item.section}
            </p>
          ) : (
            <button
              type="button"
              className={`lateral-menu__item${
                location.pathname === item.url
                  ? ' lateral-menu__item--active'
                  : ''
              }`}
              key={item.key}
              aria-current={location.pathname === item.url ? 'page' : undefined}
              onClick={() => redirectUrl(item.url)}
            >
              {item.icon}
              <span className="lateral-menu__label">{item.title}</span>
              {item.badge > 0 && (
                <span
                  className="lateral-menu__badge"
                  aria-label={t('chat.unreadCount', { count: item.badge })}
                >
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </button>
          )
        )}

        {!isB2B && !isUserAdmin(userRole) && isB2BLandingPage && (
          <OrangeButton
            className="d-flex gap-8"
            onClick={() => {
              trackB2BEvent('b2b_agency_signup_click', {
                section: 'mobile-menu',
              });
              redirectUrl(B2B_ROUTES.TRAVEL_PARTNERS_JOIN);
            }}
          >
            {t('b2b.landing.header.joinAgency')}
          </OrangeButton>
        )}

        {!isB2B && !isUserAdmin(userRole) && !isB2BLandingPage && (
          <OrangeButton
            className="d-flex gap-8"
            onClick={() => redirectUrl(PUBLIC_ROUTES.SEARCH_TRAVEL_EXPERT)}
          >
            {t('sidebar.findGuide')}
          </OrangeButton>
        )}
      </div>

      <MenuFooter />
    </Drawer>
  );
}
